/**
 * GLOBAL UTILITY: Format session ID as GUID by adding dashes in correct places
 * GUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (8-4-4-4-12)
 *
 * Use this function wherever Five9 session IDs need to be processed to ensure
 * consistent GUID formatting across the application.
 */
export const formatSessionIdAsGuid = (sessionId: string): string => {
	// Handle null/undefined/empty
	if (!sessionId || typeof sessionId !== "string") {
		console.warn("⚠️ [GUID_FORMAT] Invalid session ID provided:", sessionId);
		return sessionId || "";
	}

	// If already has dashes, return as-is
	if (sessionId.includes("-")) {
		return sessionId;
	}

	// Remove any existing dashes and ensure we have a 32-character string
	const cleanId = sessionId.replace(/-/g, "");

	// If not exactly 32 characters, return original (not a valid GUID)
	if (cleanId.length !== 32) {
		console.warn(
			`⚠️ [GUID_FORMAT] Session ID is not 32 characters: ${cleanId.length} chars - returning as-is`,
		);
		return sessionId;
	}

	// Insert hyphens at positions 9, 13, 17, 21 (equivalent to SQL STUFF operations)
	// Working backwards like SQL: 21 -> 17 -> 13 -> 9
	let result = cleanId;
	result = `${result.substring(0, 20)}-${result.substring(20)}`; // Position 21 (0-based: 20)
	result = `${result.substring(0, 16)}-${result.substring(16)}`; // Position 17 (0-based: 16)
	result = `${result.substring(0, 12)}-${result.substring(12)}`; // Position 13 (0-based: 12)
	result = `${result.substring(0, 8)}-${result.substring(8)}`; // Position 9 (0-based: 8)

	console.log(`🔧 [GUID_FORMAT] Formatted: ${sessionId} → ${result}`);
	return result;
};

// Make GUID formatting globally available for use throughout the application
if (typeof window !== "undefined") {
	(window as any).formatSessionIdAsGuid = formatSessionIdAsGuid;
}
